import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { MailFileDto, MailFilesWithRightsDTO } from '../interfaces';
import {
  MailFileParams,
  MailsFilesAcceptValidationParams,
  MailsFilesRefuseSignatureParams,
  MailsFilesRefuseValidationParams,
} from 'src/app/shared/models/mails/mail-file';
import {
  MailFilesToValidateDTO,
  MailPreviousValidationDTO,
  MailValidationSigningHistoryDTO,
} from '../interfaces/mails/mail-validation-dto.interface';
import { PastellHistoryDTO } from '../interfaces/mails/mail-pastell-history-dto.interface';
import { MailsFilesToSignIndividuallyDTO } from '../interfaces/mails/mail-signature-dto.interface';
import { MailPublipostageParams } from 'src/app/shared/models/mails/mail-publipostage';
import { RapportDocumentDTO, RapportDocumentFilesValidationSignatureParams } from 'src/app/shared/models/iCourrier/rapport-document/rapport-document';
import { AssignPastellCircuitParams } from 'src/app/shared/models/iCourrier/pastell/assign-pastell-circuit';
import { v4 as uuidv4 } from 'uuid';
import { Document } from 'src/app/shared/models/iBox/document';
import { environment } from 'src/environments/environment';
import { DocumentInput } from '../models';

@Injectable({
  providedIn: 'root',
})
export class FilesService {
  private iCourrierURL;

  constructor(private http: HttpClient) {
    const config = environment;
    this.iCourrierURL = config.apiICourrierBaseUrl;
  }

  public fetchMailFiles(mailId: number): Observable<MailFileDto[]> {
    return this.http.get<MailFileDto[]>(`${this.iCourrierURL}/Mails/${mailId}/Files`);
  }

  public getMailsFileById(id: number, fileId: number): Observable<MailFileDto> {
    return this.http.get<MailFileDto>(`${this.iCourrierURL}/Mails/${id}/Files/${fileId}`);
  }

  public patchDescriptionFile(mailId: number, fileID: number, data: MailFileParams): Observable<void> {
    return this.http.patch<void>(`${this.iCourrierURL}/Mails/${mailId}/Files/${fileID}`, data);
  }

  public getMailsFilesWithRights(id: number): Observable<MailFilesWithRightsDTO[]> {
    return this.http.get<MailFilesWithRightsDTO[]>(`${this.iCourrierURL}/Mails/${id}/FilesWithRights`);
  }

  public deleteMailFile(id: number, fileId: number): Observable<void> {
    return this.http.delete<void>(`${this.iCourrierURL}/Mails/${id}/Files/${fileId}`);
  }

  public setAsMainDocument(courrierId: number, fileId: number): Observable<void> {
    return this.http.put<void>(`${this.iCourrierURL}/Mails/${courrierId}/Files/${fileId}/SetPrincipal`, {});
  }

  //#region Validation / Signatures

  public getAllFilesToValidate(): Observable<MailFilesToValidateDTO[]> {
    return this.http.get<MailFilesToValidateDTO[]>(`${this.iCourrierURL}/Mails/Files/ToValidate`);
  }

  public getAllFilesToSignIndividuels(): Observable<MailsFilesToSignIndividuallyDTO[]> {
    return this.http.get<MailsFilesToSignIndividuallyDTO[]>(`${this.iCourrierURL}/Mails/Files/ToSignIndividuels`);
  }

  public fetchMailFilesToValidate(mailId: number): Observable<MailFileDto[]> {
    return this.http.get<MailFileDto[]>(`${this.iCourrierURL}/Mails/${mailId}/Files/ToValidate`);
  }

  public fetchMailFilesToSignIndividually(mailId: number): Observable<MailFileDto[]> {
    return this.http.get<MailFileDto[]>(`${this.iCourrierURL}/Mails/${mailId}/Files/ToSignIndividuels`);
  }

  public putAcceptValidation(data: MailsFilesAcceptValidationParams, validationLot = true): Observable<void> {
    return this.http.put<void>(`${this.iCourrierURL}/Mails/Files/AcceptValidation?validationLot=${validationLot}`, data);
  }

  public putRefuseValidation(data: MailsFilesRefuseValidationParams): Observable<void> {
    return this.http.put<void>(`${this.iCourrierURL}/Mails/Files/RefuseValidation`, data);
  }

  public putRefuseSignature(id: number, fileId: number, data: MailsFilesRefuseSignatureParams): Observable<void> {
    return this.http.put<void>(`${this.iCourrierURL}/Mails/${id}/Files/${fileId}/RefuseSignature`, data);
  }

  public getHistoriqueValidationSignature(mailID: number, fileID: number): Observable<MailValidationSigningHistoryDTO[]> {
    return this.http.get<MailValidationSigningHistoryDTO[]>(`${this.iCourrierURL}/Mails/${mailID}/Files/${fileID}/ValidationSigningHistory`);
  }
  public getValidationSignatureMails(data: RapportDocumentFilesValidationSignatureParams): Observable<RapportDocumentDTO[]> {
    let params = new HttpParams();
    Object.keys(data).forEach((key) => {
      if (data[key] !== null) {
        params = params.set(key, data[key]);
      }
    });
    return this.http.get<RapportDocumentDTO[]>(`${this.iCourrierURL}/Mails/Me/Files/ValidationSignature?${params}`);
  }

  public getPreviousValidation(mailId: number, fileId: number): Observable<MailPreviousValidationDTO[]> {
    return this.http.get<MailPreviousValidationDTO[]>(`${this.iCourrierURL}/Mails/${mailId}/Files/${fileId}/ValidationPrevSteps`);
  }

  //#endregion

  //#region pastell

  public getPastellStatutes(): Observable<
    {
      status: string;
      description: string;
    }[]
  > {
    return this.http.get<
      {
        status: string;
        description: string;
      }[]
    >(`${this.iCourrierURL}/Global/Fichiers/PastellStatutes`);
  }

  public addDocumentPastell(
    id: number,
    document: DocumentInput,
    lancerValidation?: boolean,
    qstValidationAnnulee?: boolean,
    procedureValidationQuestionPosees?
  ): Observable<MailFileDto[]> {
    const body = new FormData();
    for (const property in document) {
      if (Object.prototype.hasOwnProperty.call(document, property)) {
        if (property !== 'file') {
          body.append(property, document[property]);
        } else {
          body.append('file', document.file, document.description + '.rtf');
        }
      }
    }
    if (procedureValidationQuestionPosees && procedureValidationQuestionPosees.length > 0) {
      body.append('ProcedureValidationQuestionPosees', JSON.stringify(procedureValidationQuestionPosees));
    }
    return this.http.post<MailFileDto[]>(
      `${this.iCourrierURL}/Mails/${id}/Files/AddPastellDocument?lancerValidation=${lancerValidation}&qstValidationAnnulee=${qstValidationAnnulee}`,
      body
    );
  }

  public assignCircuitPastell(mailID: number, fileID: number, data: AssignPastellCircuitParams): Observable<void> {
    return this.http.patch<void>(`${this.iCourrierURL}/Mails/${mailID}/Files/${fileID}/AssignPastellCircuit`, data);
  }

  public getHistoriquePastell(mailID: number, fileID: number): Observable<PastellHistoryDTO[]> {
    return this.http.get<PastellHistoryDTO[]>(`${this.iCourrierURL}/Mails/${mailID}/Files/${fileID}/PastellHistory`);
  }

  //#endregion

  //#region publipostage

  public getTemplatePublipostage(): Observable<Blob> {
    return this.http.get('./assets/lib/Template_pour _publipostage.xlsx', { responseType: 'blob' });
  }

  public postPublipostage(courrierID: number, fileID: number, data: MailPublipostageParams): Observable<void> {
    return this.http.post<void>(`${this.iCourrierURL}/Mails/${courrierID}/Files/${fileID}/Publipostage`, data);
  }

  //#endregion

  //#region Documents
  public transformDocument(id: number, rtfDocument: Blob, documentName: string, fusionDestPrincipal = false, modeleId?: number): Observable<Blob> {
    const body = new FormData();
    body.append('RtfDocumentFile', rtfDocument, documentName + '.rtf');
    body.append('Guid', uuidv4());
    if (modeleId) {
      body.append('ModeleId', modeleId.toString());
    }
    return this.http.post(`${this.iCourrierURL}/Mails/${id}/Files/TransformDocument?fusionDestPrincipal=${fusionDestPrincipal}`, body, {
      responseType: 'blob',
    });
  }

  public addDocument(
    id: number,
    document: DocumentInput,
    lancerValidation: boolean = false,
    qstValidationAnnulee: boolean = false,
    procedureValidationQuestionPosees = []
  ): Observable<MailFileDto[]> {
    const body = new FormData();
    for (const property in document) {
      if (Object.prototype.hasOwnProperty.call(document, property)) {
        if (property !== 'file') {
          body.append(property, document[property]);
        } else {
          body.append('file', document.file, document.description + '.rtf');
        }
      }
    }
    if (procedureValidationQuestionPosees?.length > 0) {
      body.append('ProcedureValidationQuestionPosees', JSON.stringify(procedureValidationQuestionPosees));
    }

    return this.http.post<MailFileDto[]>(
      `${this.iCourrierURL}/Mails/${id}/Files/AddDocument?lancerValidation=${lancerValidation}&qstValidationAnnulee=${qstValidationAnnulee}`,
      body
    );
  }

  public addDocumentVersion(
    id: number,
    fileId: number,
    document: DocumentInput,
    lancerValidation?: boolean,
    qstValidationAnnulee?: boolean,
    procedureValidationQuestionPosees?
  ): Observable<MailFileDto[]> {
    const body = new FormData();
    for (const property in document) {
      if (Object.prototype.hasOwnProperty.call(document, property)) {
        if (property !== 'file') {
          body.append(property, document[property]);
        } else {
          body.append('file', document.file, document.description + '.rtf');
        }
      }
    }
    if (procedureValidationQuestionPosees?.length > 0) {
      body.append('ProcedureValidationQuestionPosees', JSON.stringify(procedureValidationQuestionPosees));
    }
    return this.http.post<MailFileDto[]>(
      `${this.iCourrierURL}/Mails/${id}/Files/${fileId}/Versions/AddDocument?lancerValidation=${lancerValidation}&qstValidationAnnulee=${qstValidationAnnulee}`,
      body
    );
  }

  //#endregion

  public addMailFiles(id: number, document: Document, file: File, extension: string, sourceType?: string): Observable<MailFileDto> {
    const body = new FormData();
    for (const property in document) {
      if (Object.prototype.hasOwnProperty.call(document, property)) {
        body.append(property, document[property]);
      }
    }
    body.append('file', file, file.name);
    let editeurSourceType;
    sourceType ? (editeurSourceType = sourceType) : (editeurSourceType = 'Externe');
    return this.http.post<MailFileDto>(`${this.iCourrierURL}/Mails/${id}/Files?extension=${extension}&editeurSourceType=${editeurSourceType}`, body);
  }

  public getAllVersionsOfFile(mailId: number, fileId: number): Observable<MailFileDto[]> {
    return this.http.get<MailFileDto[]>(`${this.iCourrierURL}/Mails/${mailId}/Files/${fileId}/Versions`);
  }

  public restoreFile(mailId: number, fileId: number, previousVersionFileId: number): Observable<MailFileDto> {
    return this.http.post<MailFileDto>(
      `${this.iCourrierURL}/Mails/${mailId}/Files/${fileId}/RestoreVersion?previousVersionFileId=${previousVersionFileId}`,
      null
    );
  }
}
